import { useContext } from 'react';

import { Button, UseDisclosureReturn } from '@chakra-ui/react';
import { Formik } from 'formik';
import { toast } from 'react-toastify';
import { object } from 'yup';

import { OriginalLessee, useUpsertOriginalLesseeMutation } from '../../../gql/generated/graphql';

import Modal from '../../shared/Modal';
import Tooltip from '../../shared/Tooltip';

import { DealActionsEnum, DealContext } from '../../../libs/DealContext';
import { originalLesseeRequiredValidation } from '../../../utils/validation/originalLessee';
import OriginalLesseeForm from '../../CreditApplication/OriginalLesseeForm';
import { cleanOriginalLessee } from '../../CreditApplication/utils';

type RequiredOriginalLesseeModalProps = UseDisclosureReturn & {
  onGenerateDocs: () => void;
};

const RequiredOriginalLesseeModal = ({
  isOpen,
  onClose,
  onGenerateDocs,
}: RequiredOriginalLesseeModalProps) => {
  const { deal, dispatch } = useContext(DealContext);

  const [upsertOriginalLessee, { loading: upsertLoading }] = useUpsertOriginalLesseeMutation();

  const handleSubmit = async (values: { original_lessee: OriginalLessee }) => {
    if (!deal.id || !values) {
      return;
    }

    try {
      const cleanedValues = cleanOriginalLessee({ original_lessee: values.original_lessee });
      const result = await upsertOriginalLessee({
        variables: {
          deal_id: Number(deal.id),
          original_lessee: cleanedValues,
        },
      });

      dispatch({
        type: DealActionsEnum.DeepUpdateDeal,
        payload: {
          original_lessee: result.data?.upsertOriginalLessee,
        },
      });

      onGenerateDocs();
    } catch (error) {
      toast.error('Error updating Original Lessee');
    }
  };

  return (
    <Formik
      enableReinitialize
      validateOnMount
      onSubmit={handleSubmit}
      initialValues={{ original_lessee: deal?.original_lessee ?? {} }}
      validationSchema={object().shape({
        original_lessee: originalLesseeRequiredValidation,
      })}
    >
      {({ handleChange, isValid, errors }) => (
        <Modal
          title="Original Lessee Info"
          isOpen={isOpen}
          onClose={onClose}
          size="2xl"
          hasForm
          canDismiss={!upsertLoading}
          leftButtons={
            <Button variant="secondary" onClick={onClose} isDisabled={upsertLoading}>
              CANCEL
            </Button>
          }
          rightButtons={
            <Tooltip errors={errors.original_lessee}>
              <Button type="submit" isDisabled={!isValid} isLoading={upsertLoading}>
                GENERATE DOCS
              </Button>
            </Tooltip>
          }
        >
          <OriginalLesseeForm customHandleChange={handleChange} mb={5} />
        </Modal>
      )}
    </Formik>
  );
};

export default RequiredOriginalLesseeModal;
